<script setup lang="ts">
/**
 * 🔴 **THE ORGANISATION SWITCHER** — the first thing on the sidebar (D37, D41, D42).
 *
 * Metaship's, in Pinksy's vocabulary: the company's name at the top, and **"Change organisation"**
 * underneath it. Devin's instruction was to bring it in rather than diverge — *"no bring them in
 * change the pinksy so that its the same as metaship"* — and the shape is the reason it works. A
 * person on a collaborative platform is always acting FOR somebody, and the answer to "for whom?"
 * belongs where their eye already is, not behind a settings screen.
 *
 * Choosing one is a **client-side change of a header** (`auth/http.ts`). No re-authentication, no new
 * token, no round trip: identity is stable, the organisation is a choice.
 *
 * 🔴 **EACH COMPANY IS DRAWN IN ITS OWN COLOURS** (PNK-109). Devin: *"when an org is selected or
 * viewed it's got to get their look and feel."* This is the highest-frequency company name in the
 * product — top of the sidebar, on every route, all day — and until now every row was two initials on
 * the tenant's one colour, so twelve companies were twelve identical tiles. A producer with three
 * switches between them constantly; their own mark and their own accent is the difference between
 * reading a list and recognising a row.
 *
 * 🔴 **THROUGH THE ONE BADGE, NEVER GREY** (D164 rule 5). Every company here is a `PinkCompanyBadge` — the
 * same object a person card and her own pages draw — in its palette, else the accent read off its logo,
 * else its name's tint. A logo-only agency like APM is never drawn in the tenant's colour.
 *
 * 🔴 **THEIR PALETTE REACHES THE BADGE AND STOPS THERE** (D28). It is bound as custom properties on
 * that one element, never on `:root` and never through the Vuetify theme: skinning the product is the
 * TENANT row's job, and a supplier's green on `:root` would turn the whole Desk green. Nothing here
 * ranks, filters or sorts on a colour (D15) — it is a picture a human looks at.
 *
 * Three things this deliberately does NOT do:
 *
 *   - **It does not hide a suspended company.** A place a person knows they work, silently absent from
 *     their own list, reads as the platform having lost it. It is drawn, greyed, with the reason —
 *     which is the true thing and the kinder one.
 *   - **It does not offer a menu when there is nothing to switch to.** One selectable company is not a
 *     choice; the name is still context and is still drawn, but nothing invites an act that cannot go
 *     anywhere.
 *   - **It never shows a uuid.** Not in the row, not in a tooltip. A person recognises "Wilder Hearts".
 */
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { PinkCompanyBadge, useMarks } from "@pinksy/ui";
import { fetchOrganisationMark, type OrganisationChoice } from "@/organisation/organisationApi";

const props = defineProps<{
  /** Every organisation this person belongs to, selectable or not. */
  choices: OrganisationChoice[];
  /** The one being acted for, or null before a choice is possible. */
  chosen: OrganisationChoice | null;
  /** True once a list has been answered — false draws a placeholder, never "no organisations". */
  ready: boolean;
  /** Collapsed sidebar: the badge only, with the name as its title. */
  rail: boolean;
  /**
   * 🔴 The agencies that REPRESENT this person (D164) — never companies she works at, never selectable. Drawn
   * only when she acts for no company: then they are who she is here, not "no organisation". Any number of them.
   */
  agencies?: OrganisationChoice[];
}>();

const emit = defineEmits<{ (event: "choose", organisationId: string): void }>();

const { t } = useI18n();
const open = ref(false);
const query = ref("");

/**
 * The search box, which appears only when the list is long enough to need one.
 *
 * Four is where scanning stops being faster than typing. Below it a search box is furniture: one more
 * thing to read on the way to a list you can already see all of.
 */
const SEARCH_THRESHOLD = 5;
const searchable = computed(() => props.choices.length >= SEARCH_THRESHOLD);

/** Matches the trading name OR the legal one — a person may know a company by either. */
const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase();
  if (needle === "") return props.choices;
  return props.choices.filter(
    (choice) => choice.name.toLowerCase().includes(needle) || choice.legalName.toLowerCase().includes(needle),
  );
});

const selectableCount = computed(() => props.choices.filter((choice) => choice.selectable).length);
const canSwitch = computed(() => selectableCount.value > 1);

/**
 * 🔴 **THE MARKS, FETCHED** (D77, D94) — `useMarks` is the kit's one implementation of this, and the
 * shell hands it the only line that ever differs between callers: its own authenticated client. A
 * logo is served to a SESSION, so the bytes come back as an object URL that lives as long as the
 * sidebar does. A company with no `logoSha256` is never asked for, and a refusal is answered once.
 *
 * 🔴 It also reads each mark's ACCENT (D164 rule 5), once per logo — the badge draws a logo-only company in
 * it rather than in the tenant's colour.
 */
const agencyRows = computed<OrganisationChoice[]>(() => props.agencies ?? []);
/** A represented person in no company: her agencies are drawn instead of the "no organisation" prompt. */
const represented = computed(() => props.chosen === null && agencyRows.value.length > 0);
const agencyNames = computed(() => agencyRows.value.map((agency) => agency.name).join(", "));
/** What an agency represents her for — the edge's position ("Voice & commercials") where one was recorded. */
const agencyCaption = (agency: OrganisationChoice): string | null => {
  const position = (agency.position ?? "").trim();
  return position === "" ? null : position;
};

const { urls: marks, accents } = useMarks(
  () => [...props.choices, ...agencyRows.value].map((choice) => ({ id: choice.id, sha256: choice.logoSha256 })),
  { load: fetchOrganisationMark },
);

/** Their mark once the bytes are here — null until then, and for most companies forever. */
const markOf = (choice: OrganisationChoice): string | null => marks.value.get(choice.id) ?? null;
/** The accent their mark gave, below their palette — null until it has been read. */
const accentOf = (choice: OrganisationChoice): string | null => accents.value.get(choice.id) ?? null;

/**
 * The line under the name: what this person IS to the company (D22) and their title where they have
 * one. Not the standing — an ACTIVE company saying "ACTIVE" is noise; only the exceptional standing
 * is worth a person's attention, and that is drawn on the row it applies to.
 */
const subtitle = computed(() => {
  if (props.chosen === null) return null;
  const parts = [props.chosen.position, t(`organisation.relationship.${props.chosen.relationship}`, props.chosen.relationship)];
  return parts.filter((part): part is string => typeof part === "string" && part !== "").join(" · ");
});

/** Why a row cannot be entered, in the words a person can act on. */
const blockedReason = (choice: OrganisationChoice): string =>
  t(`organisation.standing.${choice.standing}`, t("organisation.standing.blocked"));

function onChoose(choice: OrganisationChoice): void {
  if (!choice.selectable) return;
  emit("choose", choice.id);
  open.value = false;
}

// A fresh search every time it opens: a needle left over from last time hides the list a person came
// back to read.
watch(open, (isOpen) => {
  if (!isOpen) query.value = "";
});
</script>

<template>
  <div class="sh-org" :class="{ 'sh-org--rail': rail }" data-testid="organisation-switcher">
    <!-- Before the first answer: the shape of the row, with nothing claimed. Drawing "no
         organisations" at somebody who has three is worse than drawing nothing at all. -->
    <div v-if="!ready" class="sh-org__row sh-org__row--waiting" aria-hidden="true">
      <span class="sh-org__badge sh-org__badge--waiting"></span>
      <span v-if="!rail" class="sh-org__id">
        <span class="sh-org__skeleton sh-org__skeleton--name"></span>
        <span class="sh-org__skeleton sh-org__skeleton--sub"></span>
      </span>
    </div>

    <!-- 🔴 A person who belongs to no LIVE company. Every organisation-scoped endpoint refuses in
         this state, so the sidebar says so plainly instead of leaving a blank where a company goes
         and letting each screen fail on its own. -->
    <!-- 🔴 Represented, and working at no company (D164): who she is here is her agencies, each in its own
         colours — never "ask an administrator", because nobody needs to add her anywhere. -->
    <div
      v-else-if="represented"
      class="sh-org__row sh-org__row--represented"
      :title="rail ? t('organisation.representedBy', { agencies: agencyNames }) : undefined"
      data-testid="organisation-represented"
    >
      <!-- The collapsed rail has no room for words: each agency's mark alone, the names on the row's title. -->
      <span v-if="rail" class="sh-org__agencies">
        <PinkCompanyBadge
          v-for="agency in agencyRows"
          :key="`${agency.id}-${agency.relationship}`"
          :organisation="agency"
          :src="markOf(agency)"
          :derived="accentOf(agency)"
          size="sm"
          mark-only
        />
      </span>
      <!-- 🔴 With room for names, EACH agency is a full badge — its name in its own face, tied to its own mark —
           with what it represents her for where that is known (D164 rules 2e, 5). Never a joined list of names. -->
      <span v-else class="sh-org__id">
        <span class="sh-org__name">{{ t("organisation.representedHeading") }}</span>
        <span class="sh-org__agency-list" data-testid="organisation-agencies">
          <PinkCompanyBadge
            v-for="agency in agencyRows"
            :key="`${agency.id}-${agency.relationship}`"
            :organisation="agency"
            :src="markOf(agency)"
            :derived="accentOf(agency)"
            :caption="agencyCaption(agency)"
            size="sm"
          />
        </span>
      </span>
    </div>

    <div v-else-if="chosen === null" class="sh-org__row sh-org__row--none" :title="t('organisation.noneTitle')">
      <span class="sh-org__badge sh-org__badge--none"><v-icon size="15">domain_disabled</v-icon></span>
      <span v-if="!rail" class="sh-org__id">
        <span class="sh-org__name sh-org__name--none">{{ t("organisation.none") }}</span>
        <span class="sh-org__sub">{{ t("organisation.noneHint") }}</span>
      </span>
    </div>

    <!-- One company: context, not a control. No menu, no chevron, nothing to press. -->
    <div v-else-if="!canSwitch" class="sh-org__row" :title="rail ? chosen.name : undefined">
      <!-- 🔴 THE ONE BADGE (D164 rule 5): their mark, their accent, their face — the same object a person card
           and her own pages draw. The rail keeps the mark alone, with the name on the row's title. -->
      <PinkCompanyBadge :organisation="chosen" :src="markOf(chosen)" :derived="accentOf(chosen)" :caption="subtitle" :mark-only="rail" size="md" class="sh-org__company" />
    </div>

    <!-- More than one: the switcher proper. -->
    <v-menu v-else v-model="open" :location="rail ? 'right' : 'bottom start'" :close-on-content-click="false" offset="6">
      <template #activator="{ props: activator }">
        <button
          type="button"
          class="sh-org__row sh-org__row--button"
          :class="{ 'sh-org__row--open': open }"
          :title="rail ? chosen.name : undefined"
          data-testid="organisation-switcher-activator"
          v-bind="activator"
        >
          <PinkCompanyBadge :organisation="chosen" :src="markOf(chosen)" :derived="accentOf(chosen)" :mark-only="rail" size="md" class="sh-org__company">
            <!-- 🔴 Metaship's own words, and they earn the space: "Change organisation" says both
                 where you are and that you may leave, which a bare chevron does not. -->
            <template #caption>
              <span class="sh-org__change">
                {{ t("organisation.change") }}
                <v-icon size="11" class="sh-org__chev">unfold_more</v-icon>
              </span>
            </template>
          </PinkCompanyBadge>
          <v-icon v-if="rail" size="11" class="sh-org__chev sh-org__chev--rail">unfold_more</v-icon>
        </button>
      </template>

      <div class="sh-orgmenu">
        <div class="sh-orgmenu__title">{{ t("organisation.pick") }}</div>

        <div v-if="searchable" class="sh-orgmenu__search">
          <v-icon size="15" class="sh-orgmenu__search-icon">search</v-icon>
          <input
            v-model="query"
            type="search"
            class="sh-orgmenu__input"
            :placeholder="t('organisation.search')"
            :aria-label="t('organisation.search')"
          />
        </div>

        <div class="sh-orgmenu__list">
          <button
            v-for="choice in filtered"
            :key="choice.id"
            type="button"
            class="sh-orgmenu__row"
            :class="{
              'sh-orgmenu__row--current': choice.id === chosen.id,
              'sh-orgmenu__row--blocked': !choice.selectable,
            }"
            :disabled="!choice.selectable"
            :title="choice.selectable ? undefined : blockedReason(choice)"
            @click="onChoose(choice)"
          >
            <PinkCompanyBadge :organisation="choice" :src="markOf(choice)" :derived="accentOf(choice)" size="sm" class="sh-orgmenu__company">
              <template v-if="(choice.legalName && choice.legalName !== choice.name) || !choice.selectable" #caption>
                <!-- The registered name only where it differs — repeating it is noise. -->
                <span v-if="choice.legalName && choice.legalName !== choice.name" class="sh-orgmenu__legal">{{ choice.legalName }}</span>
                <span v-if="!choice.selectable" class="sh-orgmenu__blocked">{{ blockedReason(choice) }}</span>
              </template>
            </PinkCompanyBadge>
            <v-icon v-if="choice.id === chosen.id" size="16" class="sh-orgmenu__tick">check</v-icon>
          </button>

          <div v-if="filtered.length === 0" class="sh-orgmenu__empty">{{ t("organisation.noMatch") }}</div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style scoped>
.sh-org {
  padding: 2px 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--sh-hairline);
}
.sh-org--rail {
  padding: 2px 0 10px;
}

.sh-org__row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  color: inherit;
  font: inherit;
}
.sh-org--rail .sh-org__row {
  justify-content: center;
}
.sh-org__row--button {
  cursor: pointer;
  border-radius: 9px;
  padding: 3px 4px;
  margin: -3px -4px;
  transition:
    background-color 140ms ease,
    transform 140ms ease;
}
.sh-org__row--button:hover,
.sh-org__row--open {
  background: var(--sh-hover);
}
.sh-org__row--button:active {
  transform: scale(0.985);
}

/* The placeholder tile where there is no company to draw — waiting, or none. A company itself is a
   `PinkCompanyBadge` (D164 rule 5), whose colours come from `companyLook()` and never from here. */
.sh-org__badge {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
}
.sh-org__company {
  min-width: 0;
  color: var(--sh-text);
}
.sh-org__agencies {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  flex-shrink: 0;
}
.sh-org__agency-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  min-width: 0;
}
.sh-org--rail .sh-org__agencies {
  flex-direction: column;
}
.sh-org__badge--none {
  color: var(--sh-text-60);
  background: var(--sh-hover);
}
.sh-org__badge--waiting {
  background: var(--sh-hover);
}

.sh-org__id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}
.sh-org__name {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-org__name--none {
  font-weight: 600;
  color: var(--sh-text-60);
}
.sh-org__sub {
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--sh-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* "Change organisation" — quieter than the name, and it is the affordance, so it
   lights up with the row rather than waiting for its own hover. */
.sh-org__change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--sh-text-60);
  transition: color 140ms ease;
}
.sh-org__row--button:hover .sh-org__change,
.sh-org__row--open .sh-org__change {
  color: rgb(var(--v-theme-primary));
}
.sh-org__chev {
  opacity: 0.75;
}
.sh-org__chev--rail {
  position: absolute;
  right: 4px;
  opacity: 0.5;
}

/* Waiting: the row's shape, nothing claimed. */
.sh-org__skeleton {
  display: block;
  height: 8px;
  border-radius: 4px;
  background: var(--sh-hover);
}
.sh-org__skeleton--name {
  width: 92px;
  height: 10px;
  margin-bottom: 4px;
}
.sh-org__skeleton--sub {
  width: 58px;
}

/* ── The menu ──────────────────────────────────────────────────────────── */
.sh-orgmenu {
  min-width: 244px;
  max-width: 320px;
  padding: 6px;
  border-radius: 12px;
  background: var(--sh-surface, rgb(var(--v-theme-surface)));
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.18);
}
.sh-orgmenu__title {
  padding: 6px 8px 4px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--sh-text-45, var(--sh-text-60));
}
.sh-orgmenu__search {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 2px 4px 6px;
  padding: 5px 8px;
  border-radius: 8px;
  background: var(--sh-hover);
}
.sh-orgmenu__search-icon {
  color: var(--sh-text-60);
}
.sh-orgmenu__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: none;
  font-size: 12.5px;
  color: var(--sh-text);
}
.sh-orgmenu__list {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
}
.sh-orgmenu__row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  border: 0;
  border-radius: 9px;
  background: none;
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.sh-orgmenu__row:hover:not(:disabled) {
  background: var(--sh-hover);
}
.sh-orgmenu__row--current {
  background: rgba(var(--v-theme-primary), 0.09);
}
/* Blocked: legible, and plainly not a target. Never hidden — see the header note. */
.sh-orgmenu__row--blocked {
  cursor: not-allowed;
  opacity: 0.55;
}
.sh-orgmenu__company {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--sh-text);
}
.sh-orgmenu__legal,
.sh-orgmenu__blocked {
  display: block;
  font-size: 10px;
  font-weight: 500;
  color: var(--sh-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-orgmenu__blocked {
  color: rgb(var(--v-theme-warning, var(--v-theme-primary)));
  font-weight: 650;
}
.sh-orgmenu__tick {
  flex: 0 0 auto;
  color: rgb(var(--v-theme-primary));
}
.sh-orgmenu__empty {
  padding: 10px 8px;
  font-size: 12px;
  color: var(--sh-text-60);
}
</style>
